import React from "react";
import {
  AppWindow,
  Package,
  Activity,
  ShieldAlert,
  Building2,
  GitBranch,
  AlignLeft,
  Edit2,
  Trash2,
  Layers,
  Network,
} from "lucide-react";
import { CMDBApplication } from "../../../../api/frameworksApi";
import { StatusBadge } from "../cmdbUtils";
import { Tip } from "../../../components/ui/tooltip";

interface AppInfoCardProps {
  application: CMDBApplication;
  onEdit?: () => void;
  onDelete?: () => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

interface InfoTileProps {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  children: React.ReactNode;
  /** Full text shown in a tooltip on hover (for values that may truncate). */
  tooltip?: string;
}

function InfoTile({ icon, iconBg, label, children, tooltip }: InfoTileProps) {
  const value = (
    <div className="text-sm font-semibold text-foreground leading-tight truncate">
      {children}
    </div>
  );
  return (
    <div className="flex items-center gap-3 bg-muted/30 border border-border rounded-xl px-4 py-3 min-w-0 flex-1">
      <div
        className={`flex-shrink-0 size-9 rounded-lg flex items-center justify-center ${iconBg}`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] d font-medium mb-0.5 tracking-wide">
          {label}
        </p>
        {tooltip ? (
          <Tip label={tooltip} side="top" className="block min-w-0">
            {value}
          </Tip>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

export function AppInfoCard({
  application,
  onEdit,
  onDelete,
  activeTab,
  onTabChange,
}: AppInfoCardProps) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Section header with actions */}
      <div className="flex items-center gap-2.5 px-5 py-3 border-b border-border">
        <div className="size-6 rounded-md bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
          <AppWindow className="size-3.5 text-indigo-600 dark:text-indigo-400" />
        </div>
        <span className="text-sm font-semibold text-foreground flex-1">
          App Information
        </span>

        {/* View toggle */}
        {onTabChange && (
          <div className="flex items-center bg-muted/40 border border-border rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() =>
                activeTab === "topology" && onTabChange("components")
              }
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                activeTab !== "topology"
                  ? "bg-indigo-600 text-white shadow-sm shadow-indigo-500/30"
                  : "d hover:text-foreground hover:bg-muted/60"
              }`}
            >
              <Layers className="size-3.5" />
              Overview
            </button>
            <button
              onClick={() =>
                activeTab !== "topology" && onTabChange("topology")
              }
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                activeTab === "topology"
                  ? "bg-indigo-600 text-white shadow-sm shadow-indigo-500/30"
                  : "d hover:text-foreground hover:bg-muted/60"
              }`}
            >
              <Network className="size-3.5" />
              Tree View
            </button>
          </div>
        )}

        {/* Edit / Delete */}
        {onEdit && (
          <button
            onClick={onEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-transparent rounded-lg hover:border-border hover:bg-muted transition-colors text-sm d hover:text-foreground"
          >
            <Edit2 className="size-4" /> Edit
          </button>
        )}
        {onDelete && (
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-transparent rounded-lg text-red-500 hover:border-red-500/40 hover:bg-red-500/10 transition-colors text-sm"
          >
            <Trash2 className="size-4" /> Delete
          </button>
        )}
      </div>

      {/* Info tiles row */}
      <div className="flex flex-wrap gap-3 p-4">
        {/* App Name */}
        <InfoTile
          icon={
            <Package className="size-4 text-indigo-600 dark:text-indigo-400" />
          }
          iconBg="bg-indigo-500/15"
          label="App Name"
          tooltip={application.name}
        >
          {application.name || "—"}
        </InfoTile>

        {/* Status */}
        <InfoTile
          icon={
            <Activity className="size-4 text-violet-600 dark:text-violet-400" />
          }
          iconBg="bg-violet-500/15"
          label="Status"
        >
          <StatusBadge status={application.status} />
        </InfoTile>

        {/* Criticality */}
        <InfoTile
          icon={
            <ShieldAlert className="size-4 text-amber-600 dark:text-amber-400" />
          }
          iconBg="bg-amber-500/15"
          label="Criticality"
        >
          <StatusBadge status={application.criticality} type="criticality" />
        </InfoTile>

        {/* Department */}
        {application.departmentName && (
          <InfoTile
            icon={
              <Building2 className="size-4 text-green-600 dark:text-green-400" />
            }
            iconBg="bg-green-500/15"
            label="Department"
            tooltip={application.departmentName}
          >
            {application.departmentName}
          </InfoTile>
        )}

        {/* Division */}
        {application.divisionName && (
          <InfoTile
            icon={
              <GitBranch className="size-4 text-blue-600 dark:text-blue-400" />
            }
            iconBg="bg-blue-500/15"
            label="Division"
            tooltip={application.divisionName}
          >
            {application.divisionName}
          </InfoTile>
        )}
      </div>

      {/* Description if present */}
      {application.description && (
        <div className="mx-4 mb-4 flex items-start gap-3 rounded-xl bg-muted/20 border border-border px-4 py-3">
          <AlignLeft className="size-4 d flex-shrink-0 mt-0.5" />
          <p className="text-sm text-foreground/80 leading-relaxed">
            {application.description}
          </p>
        </div>
      )}
    </div>
  );
}
